import React, { useMemo, useState } from 'react';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { projectV2ApiSlice } from 'src/api/v2/project-v2-ApiSlice';
import {
    useCreateUserManufacturerMutation,
    useGetUserManufacturerDiscountQuery,
    useUpdateUserManufacturerDiscountMutation,
} from 'src/api/v1/manufacturer-discount-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { AccountIcon } from 'src/icons/AccountIcon';
import {
    manufacturerKey,
    ProfileManufacturer,
    ProfileManufacturerAutocomplete,
} from 'src/pages/ProfileSettingsPage/components/ProfileManufacturerAutocomplete';
import { ManufacturerDiscountRow } from 'src/pages/ProfileSettingsPage/components/ManufacturerDiscountRow';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

type DiscountRow = { manufacturer: ProfileManufacturer, discount: number };

const byName = (a: DiscountRow, b: DiscountRow) => a.manufacturer.manufacturer_name.localeCompare(b.manufacturer.manufacturer_name);

export const ManufacturerDiscountsSection = () => {
    const dispatch = useAppDispatch();
    const { data: projectsResponse } = projectV2ApiSlice.useGetProjectsQuery();
    const { data: discounts, refetch: refetchDiscounts } = useGetUserManufacturerDiscountQuery();
    const [updateUserManufacturerDiscount, { isLoading: isAddLoading }] = useUpdateUserManufacturerDiscountMutation();
    const [createUserManufacturer, { isLoading: isCreateLoading }] = useCreateUserManufacturerMutation();
    const [isAddMode, setAddMode] = useState(false);

    // the legacy page reads the manufacturer list through the user's first project
    const projectId = projectsResponse?.projects[0]?.project.id;

    const rows = useMemo<DiscountRow[]>(() => {
        if (!discounts) return [];
        const internals = discounts.internal_manufacturer_discount.map(discount => ({
            manufacturer: {
                internal_product_manufacturer_id: discount.internal_product_manufacturer_id,
                manufacturer_name: discount.manufacturer_name,
            },
            discount: discount.discount_percent,
        }));
        const customs = discounts.user_manufacturer_discount.map(discount => ({
            manufacturer: {
                user_manufacturer_id: discount.user_manufacturer_id,
                manufacturer_name: discount.manufacturer_name,
            },
            discount: discount.discount_percent,
        }));
        return [...internals.sort(byName), ...customs.sort(byName)];
    }, [discounts]);

    const discountedKeys = useMemo(() => new Set(rows.map(row => manufacturerKey(row.manufacturer))), [rows]);

    const showError = () => dispatch(showSnackBar({
        text: ts('something_went_wrong_try_again', 'Something went wrong, please try again.'),
        severity: 'error',
    }));

    const addDiscount = (manufacturer: ProfileManufacturer) => {
        updateUserManufacturerDiscount({
            internal_product_manufacturer_id: manufacturer.internal_product_manufacturer_id,
            user_manufacturer_id: manufacturer.user_manufacturer_id,
            discount_percent: 0,
        })
            .unwrap()
            .then(() => refetchDiscounts())
            .catch(showError)
            .finally(() => setAddMode(false));
    };

    const createAndAddDiscount = (manufacturerName: string) => {
        createUserManufacturer({ manufacturer_name: manufacturerName })
            .unwrap()
            .then(response => addDiscount({ user_manufacturer_id: response.id, manufacturer_name: manufacturerName }))
            .catch(() => {
                showError();
                setAddMode(false);
            });
    };

    const renderContent = () => {
        if (!projectsResponse || !discounts) return null;

        if (projectId == null) {
            return (
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {ts('def_disc_failed', 'Try to create a project first!')}
                </Typography>
            );
        }

        return (
            <Stack spacing={2}>
                {rows.map(row => (
                    <ManufacturerDiscountRow
                        key={manufacturerKey(row.manufacturer)}
                        manufacturer={row.manufacturer}
                        discount={row.discount}
                    />
                ))}
                {isAddMode ? (
                    <Stack spacing={1}>
                        <Typography variant="body2">{ts('def_disc_choose_tip', 'Choose a manufacturer:')}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <ProfileManufacturerAutocomplete
                                projectId={projectId}
                                discountedKeys={discountedKeys}
                                disabled={isAddLoading || isCreateLoading}
                                onSelect={addDiscount}
                                onCreate={createAndAddDiscount}
                            />
                            <Tooltip title={ts('plt_cancel', 'Cancel')}>
                                <IconButton onClick={() => setAddMode(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                ) : (
                    <Button
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start', textTransform: 'uppercase' }}
                        onClick={() => setAddMode(true)}
                    >
                        {ts('profile_settings_add_manufacturer_discounts', 'Add manufacturer discounts')}
                    </Button>
                )}
            </Stack>
        );
    };

    return (
        <SettingsSection
            title={ts('profile_settings_manufacturer_discounts', 'Manufacturer discounts')}
            titleIcon={<AccountIcon />}
        >
            {renderContent()}
        </SettingsSection>
    );
};
